import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, Form } from '@angular/forms';
import { ConfigBasedStructuralEntryDTO, CreateConfigBasedStructuralEntryDTO, UpdateConfigBasedStructuralEntryDTO } from 'src/app/base/models/models.swagger.generated';
import { DetailsInitInfo } from '../../data-structures';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { Subscription } from 'rxjs';
import { StructuralEntryDetailsService } from './structural-entry-details.service';
import { TranslateModule } from '@ngx-translate/core';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';

import { YesNoGroupButtonComponent } from 'src/app/base/shared/yes-no-group-button/yes-no-group-button.component';

export type EntryEditFormType = {
  id: FormControl<number>;
  name: FormControl<string>;
  isSubscriptionSet: FormControl<boolean>;
};

@Component({
    selector: 'app-structural-entry-details',
    templateUrl: './structural-entry-details.component.html',
    styleUrls: ['./structural-entry-details.component.scss'],
    imports: [ReactiveFormsModule, AppErrorMessagesComponent, YesNoGroupButtonComponent, TranslateModule]
})
export class StructuralEntryDetailsComponent implements OnDestroy {
  @Input() public set detailsInitInfo(value: DetailsInitInfo) {
    const valueStr = value ? JSON.stringify(value) : null;
    const detailsInitInfoStr = this._detailInitInfo ? JSON.stringify(this._detailInitInfo) : null;
    if (valueStr != detailsInitInfoStr) {
      this.service.reset();
      this._detailInitInfo = value;
      if (value?.id) {
        this.service.getEntry(value.id).subscribe((entry: ConfigBasedStructuralEntryDTO) => {
          this._selectedEntry = entry;
          this.addEntry = false;
          this.loadFormData();
        });
      } else {
        this._selectedEntry = null;
        this.loadFormData();
        this.addEntry = true;
      }
    }
  }

  @Output() public cancel = new EventEmitter<void>();
  @Output() public entryCreated = new EventEmitter<ConfigBasedStructuralEntryDTO>();
  @Output() public entryUpdated = new EventEmitter<ConfigBasedStructuralEntryDTO>();
  @Output() public entryDeleted = new EventEmitter<number>();
  @Output() public hasPendingDetailChangesChanged = new EventEmitter<boolean>();

  public entryForm: FormGroup<EntryEditFormType>;
  public formChanged = false;

  public get isFormReady(): boolean {
    return !!this.entryForm && (!!this._selectedEntry || this.addEntry);
  }

  public get cancelButtonText(): string {
    if (this.formChanged) {
      return 'i18n.shared.cancel';
    }
    return 'i18n.shared.close';
  }

  public get isBusy(): boolean {
    return this.service.isBusy;
  }

  private addEntry = false;
  private formInitialValue;
  private _selectedEntry: ConfigBasedStructuralEntryDTO;
  private _detailInitInfo: DetailsInitInfo;
  private subscriptions = new Subscription();

  constructor(
    private service: StructuralEntryDetailsService,
    private modalService: ModalService,
    private fb: FormBuilder) {
    this.entryForm = this.fb.group<EntryEditFormType>({
      id: this.fb.control<number>(null),
      name: this.fb.control<string>(null, Validators.required),
      isSubscriptionSet: this.fb.control<boolean>(false),
    });

    this.subscriptions.add(this.entryForm.valueChanges.subscribe((v) => {
      const hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.entryForm.getRawValue()));
      this.updateFormChangedState(hasChanges);
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onCancelClicked() {
    this.cancel.emit();
  }

  public submitForm() {
    const data = this.getDTOFromFormData();
    if (this._selectedEntry != null) {
      this.service.updateEntry(data as UpdateConfigBasedStructuralEntryDTO).subscribe((entry: ConfigBasedStructuralEntryDTO) => {
        this.entryUpdated.emit(entry);
        this._selectedEntry = entry;
        this.loadFormData();
      });
    } else {
      this.service.createEntry(data as CreateConfigBasedStructuralEntryDTO).subscribe((entry: ConfigBasedStructuralEntryDTO) => {
        this.entryCreated.emit(entry);
        this.closeForm();
      });
    }
  }

  public onDelete() {
    const dialog = this.modalService.showOkCancelDialog('i18n.core.modal.delete.title', 'i18n.core.modal.delete.content');
    dialog.onOkSubject.subscribe(() => {
      this.service.deleteEntry(this._selectedEntry.id).subscribe(data => {
        const id = this._selectedEntry.id;
        this.closeForm();
        this.entryDeleted.emit(id);
      });
    });
  }

  private closeForm() {
    this.addEntry = false;
    this.updateFormChangedState(false);
    this._selectedEntry = null;
  }

  private loadFormData() {
    if (this._selectedEntry) {
      this.entryForm.reset({
        id: this._selectedEntry.id,
        name: this._selectedEntry.name ?? '',
        isSubscriptionSet: this._selectedEntry.isSubscriptionSet ?? false
      }, {onlySelf: true});
    } else {
      this.entryForm.reset();
    }
    this.formInitialValue = this.entryForm.getRawValue();
    this.updateFormChangedState(false);
  }

  private getDTOFromFormData(): CreateConfigBasedStructuralEntryDTO | UpdateConfigBasedStructuralEntryDTO {
    const rawValue = this.entryForm.value;
    let dto: CreateConfigBasedStructuralEntryDTO | UpdateConfigBasedStructuralEntryDTO;
    if (this._selectedEntry != null) {
      dto = new UpdateConfigBasedStructuralEntryDTO({
        id: this._selectedEntry.id,
        name: rawValue.name,
        isSubscriptionSet: rawValue.isSubscriptionSet ?? false
      });
    } else {
      dto = new CreateConfigBasedStructuralEntryDTO({
        parentId: this._detailInitInfo.parentId > 0 ? this._detailInitInfo.parentId : null,
        clientId: this._detailInitInfo.clientId,
        name: rawValue.name,
        isSubscriptionSet: rawValue.isSubscriptionSet ?? false
      });
    }
    return dto;
  }

  private updateFormChangedState(hasChanges: boolean) {
    if (this.formChanged !== hasChanges) {
      this.formChanged = hasChanges;
      this.hasPendingDetailChangesChanged.emit(hasChanges);
    }
  }
}
